import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import confetti from "canvas-confetti";
import { Trophy, Handshake, RotateCcw, User, Bot, Skull, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { sfx } from "@/sound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AI_WORDS = {
  pt: [
    "DUELO", "CAMPEAO", "VITORIA", "TABULEIRO", "CREDITOS", "PREMIO",
    "ARENA", "RIVAL", "JOGADA", "MESTRE", "DESAFIO", "PARTIDA",
    "ESTRATEGIA", "CORAGEM", "TROFEU", "JOGADOR", "TORNEIO", "CONQUISTA",
    "PONTUACAO", "EQUILIBRIO", "REFLEXO", "SORTE", "TALENTO", "EQUIPE",
    "FINALISTA", "PODER", "HONRA", "LENDA", "HABILIDADE", "TRIUNFO",
  ],
  en: [
    "DUEL", "CHAMPION", "VICTORY", "BOARD", "CREDITS", "PRIZE",
    "ARENA", "RIVAL", "MOVE", "MASTER", "CHALLENGE", "MATCH",
    "STRATEGY", "COURAGE", "TROPHY", "PLAYER", "TOURNAMENT", "CONQUEST",
    "SCORE", "BALANCE", "REFLEX", "LUCK", "TALENT", "TEAM",
    "FINALIST", "POWER", "HONOR", "LEGEND", "SKILL", "TRIUMPH",
  ],
  es: [
    "DUELO", "CAMPEON", "VICTORIA", "TABLERO", "CREDITOS", "PREMIO",
    "ARENA", "RIVAL", "JUGADA", "MAESTRO", "DESAFIO", "PARTIDA",
    "ESTRATEGIA", "CORAJE", "TROFEO", "JUGADOR", "TORNEO", "CONQUISTA",
    "PUNTAJE", "EQUILIBRIO", "REFLEJO", "SUERTE", "TALENTO", "EQUIPO",
    "FINALISTA", "PODER", "HONOR", "LEYENDA", "HABILIDAD", "TRIUNFO",
  ],
};

const FREQ = {
  pt: "AEORISMTNUDLCVPGBQHFJXZKWY".split(""),
  en: "ETAOINSHRDLUCMFPGWYBVKXJQZ".split(""),
  es: "EAOSRNIDLCTUMPBGVYQHFZJXKW".split(""),
};

const pickWord = (lang) => {
  const words = AI_WORDS[lang] || AI_WORDS.pt;
  return norm(words[Math.floor(Math.random() * words.length)]);
};
const ROWS = ["QWERTYUIOP", "ASDFGHJKL", "ZXCVBNM"];
const MAX_ERRORS = 6;

const norm = (w) => w.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase();
const syllables = (w) => (norm(w).match(/[AEIOU]+/g) || []).length;

const L = {
  pt: {
    intro_t: "A IA escolheu uma palavra secreta",
    intro_s: "Descubra letra por letra antes que o boneco se complete.",
    start: "Começar rodada 1",
    letters: "letras",
    syll: "sílabas",
    turn: "Sua vez — escolha uma letra",
    mistakes_left: "erros restantes",
    round_win: "Você descobriu a palavra!",
    round_lose: "O boneco foi enforcado...",
    word_was: "A palavra era",
    mistakes: "erros",
    next_round: "Próxima rodada — sua vez de escolher",
    prep2_t: "Sua vez! Escolha a palavra secreta",
    prep2_s: "A IA vai tentar descobrir. Só letras, de 3 a 10 caracteres.",
    prep2_btn: "Confirmar e desafiar a IA",
    ai_turn: "A IA está tentando descobrir...",
    final_t: "Fim de jogo",
    winner: "Vencedor",
    draw_t: "Empate!",
    pts: "pontos",
    replay: "Jogar de novo",
  },
  en: {
    intro_t: "The AI picked a secret word",
    intro_s: "Guess letter by letter before the figure is complete.",
    start: "Start round 1",
    letters: "letters",
    syll: "syllables",
    turn: "Your turn — pick a letter",
    mistakes_left: "mistakes left",
    round_win: "You found the word!",
    round_lose: "The figure was hanged...",
    word_was: "The word was",
    mistakes: "mistakes",
    next_round: "Next round — your turn to pick",
    prep2_t: "Your turn! Pick the secret word",
    prep2_s: "The AI will try to guess it. Letters only, 3–10 characters.",
    prep2_btn: "Confirm and challenge the AI",
    ai_turn: "The AI is trying to guess...",
    final_t: "Game over",
    winner: "Winner",
    draw_t: "Draw!",
    pts: "points",
    replay: "Play again",
  },
  es: {
    intro_t: "La IA eligió una palabra secreta",
    intro_s: "Adivina letra por letra antes de que se complete la figura.",
    start: "Empezar ronda 1",
    letters: "letras",
    syll: "sílabas",
    turn: "Tu turno — elige una letra",
    mistakes_left: "errores restantes",
    round_win: "¡Descubriste la palabra!",
    round_lose: "La figura fue ahorcada...",
    word_was: "La palabra era",
    mistakes: "errores",
    next_round: "Próxima ronda — tu turno de elegir",
    prep2_t: "¡Tu turno! Elige la palabra secreta",
    prep2_s: "La IA intentará adivinarla. Solo letras, de 3 a 10 caracteres.",
    prep2_btn: "Confirmar y desafiar a la IA",
    ai_turn: "La IA está intentando adivinar...",
    final_t: "Fin del juego",
    winner: "Ganador",
    draw_t: "¡Empate!",
    pts: "puntos",
    replay: "Jugar de nuevo",
  },
};

function Gallows({ errors }) {
  const part = "stroke-sky-300";
  return (
    <svg viewBox="0 0 200 220" className="w-24 sm:w-28 h-auto shrink-0">
      <g stroke="#334155" strokeWidth="5" strokeLinecap="round">
        <line x1="20" y1="205" x2="180" y2="205" />
        <line x1="60" y1="205" x2="60" y2="15" />
        <line x1="60" y1="15" x2="145" y2="15" />
        <line x1="145" y1="15" x2="145" y2="42" />
      </g>
      {errors >= 1 && <motion.circle initial={{ scale: 0 }} animate={{ scale: 1 }} cx="145" cy="58" r="16" fill="none" strokeWidth="5" className={part} style={{ transformOrigin: "145px 58px" }} />}
      {errors >= 2 && <motion.line initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} x1="145" y1="74" x2="145" y2="132" strokeWidth="5" strokeLinecap="round" className={part} />}
      {errors >= 3 && <motion.line initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} x1="145" y1="86" x2="120" y2="112" strokeWidth="5" strokeLinecap="round" className={part} />}
      {errors >= 4 && <motion.line initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} x1="145" y1="86" x2="170" y2="112" strokeWidth="5" strokeLinecap="round" className={part} />}
      {errors >= 5 && <motion.line initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} x1="145" y1="132" x2="122" y2="168" strokeWidth="5" strokeLinecap="round" className={part} />}
      {errors >= 6 && <motion.line initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} x1="145" y1="132" x2="168" y2="168" strokeWidth="5" strokeLinecap="round" className={part} />}
    </svg>
  );
}

export default function Forca() {
  const { lang } = useLang();
  const t = L[lang];

  const [phase, setPhase] = useState("start");
  const [word, setWord] = useState(() => pickWord(lang));
  const [guesses, setGuesses] = useState([]);
  const [input, setInput] = useState("");
  const [points, setPoints] = useState({ you: 0, ai: 0 });
  const roundFired = useRef(false);
  const posted = useRef(false);

  const letters = word.split("");
  const uniqueLetters = [...new Set(letters)];
  const wrong = guesses.filter((g) => !letters.includes(g));
  const errors = wrong.length;
  const correctCount = uniqueLetters.filter((l) => guesses.includes(l)).length;
  const won = uniqueLetters.every((l) => guesses.includes(l));
  const roundOver = phase.startsWith("guess") && (won || errors >= MAX_ERRORS);

  const roundPoints = (w, errs, cc) => (w ? 100 + (MAX_ERRORS - errs) * 10 : cc * 5);

  useEffect(() => {
    if (!roundOver || roundFired.current) return;
    roundFired.current = true;
    const pts = roundPoints(won, errors, correctCount);
    if (phase === "guess1") {
      setPoints((p) => ({ ...p, you: p.you + pts }));
      if (won) {
        sfx.win();
        confetti({ particleCount: 120, spread: 75, origin: { y: 0.6 }, colors: ["#38bdf8", "#2e6bff", "#fbbf24", "#ffffff"] });
      } else {
        sfx.lose();
      }
    } else if (phase === "guess2") {
      setPoints((p) => ({ ...p, ai: p.ai + pts }));
      if (won) sfx.lose();
      else sfx.win();
      setTimeout(() => setPhase("final"), 1800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundOver]);

  useEffect(() => {
    if (phase !== "guess2" || roundOver) return;
    const id = setInterval(() => {
      setGuesses((prev) => {
        const next = (FREQ[lang] || FREQ.pt).find((l) => !prev.includes(l));
        if (!next) return prev;
        return [...prev, next];
      });
      sfx.ai();
    }, 850);
    return () => clearInterval(id);
  }, [phase, roundOver, lang]);

  useEffect(() => {
    if (phase === "final" && !posted.current) {
      posted.current = true;
      const res = points.you > points.ai ? "you" : points.you < points.ai ? "ai" : "draw";
      axios.post(`${API}/score`, { result: res }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const guess = (l) => {
    if (phase !== "guess1" || roundOver || guesses.includes(l)) return;
    setGuesses((prev) => [...prev, l]);
    if (letters.includes(l)) sfx.move();
    else sfx.lose();
  };

  const startGame = () => {
    setPhase("guess1");
    sfx.move();
  };

  const confirmWord = () => {
    const w = norm(input).replace(/[^A-Z]/g, "");
    if (w.length < 3 || w.length > 10) return;
    setWord(w);
    setGuesses([]);
    setInput("");
    roundFired.current = false;
    setPhase("guess2");
    sfx.move();
  };

  const reset = () => {
    setWord(pickWord(lang));
    setGuesses([]);
    setInput("");
    setPoints({ you: 0, ai: 0 });
    roundFired.current = false;
    posted.current = false;
    setPhase("start");
  };

  const previousLang = useRef(lang);
  useEffect(() => {
    if (previousLang.current === lang) return;
    previousLang.current = lang;
    reset();
    // Reset the demo so the secret word always matches the selected language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const status =
    phase === "start"
      ? { text: t.intro_t, cls: "text-slate-300" }
      : phase === "guess1"
        ? roundOver
          ? won
            ? { text: t.round_win, cls: "text-emerald-300" }
            : { text: t.round_lose, cls: "text-red-400" }
          : { text: `${t.turn} · ${MAX_ERRORS - errors} ${t.mistakes_left}`, cls: "text-sky-300" }
        : phase === "prep2"
          ? { text: t.prep2_t, cls: "text-amber-300" }
          : phase === "guess2"
            ? { text: t.ai_turn, cls: "text-amber-300" }
            : { text: t.final_t, cls: "text-white" };

  const keyboardDisabled = phase !== "guess1" || roundOver;

  return (
    <div data-testid="forca-game" className="relative">
      <div className="absolute -inset-10 bg-[radial-gradient(circle_at_50%_40%,rgba(46,107,255,0.18),rgba(251,191,36,0.1)_45%,transparent_70%)] blur-2xl pointer-events-none" />

      <div className="relative rounded-[2rem] border border-white/10 bg-[#0c1122]/90 backdrop-blur-xl p-5 sm:p-7 shadow-[0_40px_120px_-20px_rgba(46,107,255,0.35)]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-sky-400/15 border border-sky-400/30">
              <User className="w-4 h-4 text-sky-300" />
            </span>
            <div>
              <p className="text-xs font-bold text-white leading-none">{t.you_name || "Você"}</p>
              <p className="mono text-[10px] text-sky-300 mt-1">{points.you} {t.pts}</p>
            </div>
          </div>
          <span className="mono text-[10px] uppercase tracking-[0.3em] text-slate-500">vs</span>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="text-xs font-bold text-white leading-none">DUELO IA</p>
              <p className="mono text-[10px] text-amber-300 mt-1">{points.ai} {t.pts}</p>
            </div>
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-amber-400/15 border border-amber-400/30">
              <Bot className="w-4 h-4 text-amber-300" />
            </span>
          </div>
        </div>

        <div className="relative min-h-[300px] sm:min-h-[330px]">
          <AnimatePresence mode="wait">
            {phase === "start" && (
              <motion.div
                key="start"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                className="absolute inset-0 flex flex-col items-center justify-center gap-5 text-center"
              >
                <span className="grid place-items-center w-14 h-14 rounded-2xl border border-white/10 bg-white/[0.04]">
                  <Skull className="w-7 h-7 text-amber-300" />
                </span>
                <p className="font-display font-bold text-lg text-white">{t.intro_t}</p>
                <p className="text-sm text-slate-400 max-w-[280px]">{t.intro_s}</p>
                <p className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  {word.length} {t.letters} · {syllables(word)} {t.syll}
                </p>
                <button
                  onClick={startGame}
                  data-testid="forca-start-btn"
                  className="rounded-full bg-gradient-to-r from-sky-400 to-amber-400 text-black font-display font-bold text-sm px-7 py-3 transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  {t.start}
                </button>
              </motion.div>
            )}

            {(phase === "guess1" || phase === "guess2") && (
              <motion.div
                key={`play-${phase}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                className="absolute inset-0 flex flex-col"
              >
                <div className="flex items-start gap-4">
                  <Gallows errors={errors} />
                  <div className="flex-1 pt-1">
                    <p className="mono text-[9px] uppercase tracking-[0.2em] text-slate-500 mb-2.5">
                      {word.length} {t.letters} · {syllables(word)} {t.syll}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {letters.map((l, i) => {
                        const shown = guesses.includes(l) || roundOver;
                        return (
                          <motion.span
                            key={`${phase}-${i}`}
                            initial={{ scale: 0.6, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: i * 0.04 }}
                            className={`w-7 h-9 sm:w-8 sm:h-10 rounded-lg border grid place-items-center font-display font-black text-base sm:text-lg ${
                              shown
                                ? guesses.includes(l)
                                  ? "border-sky-400/50 bg-sky-400/10 text-sky-300"
                                  : "border-red-400/50 bg-red-400/10 text-red-300"
                                : "border-white/15 bg-white/[0.04] text-transparent"
                            }`}
                          >
                            {shown ? l : "·"}
                          </motion.span>
                        );
                      })}
                    </div>
                    {errors > 0 && (
                      <p className="mono text-[9px] uppercase tracking-[0.18em] text-red-400/80 mt-3">
                        {errors} {t.mistakes}: {wrong.join(" ")}
                      </p>
                    )}
                  </div>
                </div>

                <div className={`mt-auto pt-4 flex flex-col gap-1.5 ${keyboardDisabled ? "pointer-events-none opacity-50" : ""}`}>
                  {ROWS.map((row) => (
                    <div key={row} className="flex justify-center gap-1.5">
                      {row.split("").map((l) => {
                        const used = guesses.includes(l);
                        const ok = used && letters.includes(l);
                        const bad = used && !letters.includes(l);
                        return (
                          <button
                            key={l}
                            data-testid={`forca-letter-${l}`}
                            onClick={() => guess(l)}
                            disabled={used}
                            className={`w-7 h-9 sm:w-8 sm:h-10 rounded-lg border font-display font-bold text-xs sm:text-sm transition-all duration-150 ${
                              ok
                                ? "border-emerald-400/50 bg-emerald-400/15 text-emerald-300"
                                : bad
                                  ? "border-red-500/30 bg-red-500/10 text-red-400/60"
                                  : "border-white/10 bg-white/[0.04] text-white hover:border-sky-400/50 hover:text-sky-300 active:scale-90"
                            }`}
                          >
                            {l}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>

                <AnimatePresence>
                  {phase === "guess1" && roundOver && (
                    <motion.div
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute inset-x-0 bottom-0 rounded-2xl border border-white/15 bg-[#141b33]/97 backdrop-blur p-5 text-center shadow-2xl"
                    >
                      <p className={`font-display font-black text-lg ${won ? "text-emerald-300" : "text-red-400"}`}>
                        {won ? t.round_win : t.round_lose}
                      </p>
                      <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-2">
                        {t.word_was}: <span className="text-white font-bold">{word}</span> · {errors} {t.mistakes}
                      </p>
                      <button
                        onClick={() => setPhase("prep2")}
                        data-testid="forca-next-round-btn"
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-400 text-black font-display font-bold text-xs px-6 py-3 transition-all duration-200 hover:scale-105 active:scale-95"
                      >
                        {t.next_round}
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {phase === "prep2" && (
              <motion.div
                key="prep2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                className="absolute inset-0 flex flex-col items-center justify-center gap-5 text-center"
              >
                <p className="font-display font-bold text-lg text-white">{t.prep2_t}</p>
                <p className="text-sm text-slate-400 max-w-[280px]">{t.prep2_s}</p>
                <input
                  type="password"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  maxLength={10}
                  data-testid="forca-word-input"
                  className="w-full max-w-[260px] rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-center font-display font-bold tracking-[0.3em] text-white focus:outline-none focus:ring-2 focus:ring-amber-400 uppercase"
                  placeholder="······"
                />
                <button
                  onClick={confirmWord}
                  disabled={norm(input).replace(/[^A-Z]/g, "").length < 3}
                  data-testid="forca-confirm-word-btn"
                  className="rounded-full bg-gradient-to-r from-sky-400 to-amber-400 text-black font-display font-bold text-sm px-7 py-3 transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-40"
                >
                  {t.prep2_btn}
                </button>
              </motion.div>
            )}

            {phase === "final" && (
              <motion.div
                key="final"
                data-testid="forca-final"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center"
              >
                <p className="mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{t.final_t}</p>
                <div className="flex items-center gap-8">
                  <div>
                    <p className="font-display font-black text-3xl text-sky-300">{points.you}</p>
                    <p className="mono text-[10px] uppercase tracking-widest text-slate-400 mt-1">Você</p>
                  </div>
                  <span className="mono text-slate-600">×</span>
                  <div>
                    <p className="font-display font-black text-3xl text-amber-300">{points.ai}</p>
                    <p className="mono text-[10px] uppercase tracking-widest text-slate-400 mt-1">IA</p>
                  </div>
                </div>
                {points.you === points.ai ? (
                  <p className="flex items-center gap-2 font-display font-black text-xl text-white">
                    <Handshake className="w-6 h-6 text-slate-300" />
                    {t.draw_t}
                  </p>
                ) : (
                  <p className="flex items-center gap-2 font-display font-black text-xl text-white">
                    <Trophy className="w-6 h-6 text-amber-300" />
                    {t.winner}: <span className={points.you > points.ai ? "text-sky-300" : "text-amber-300"}>{points.you > points.ai ? "Você" : "DUELO IA"}</span>
                  </p>
                )}
                <button
                  onClick={reset}
                  data-testid="forca-replay-btn"
                  className="mt-2 rounded-full bg-gradient-to-r from-sky-400 to-amber-400 text-black font-display font-bold text-sm px-7 py-3 transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  {t.replay}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p data-testid="forca-status" className={`mono text-[11px] uppercase tracking-[0.15em] ${status.cls}`}>
            {status.text}
          </p>
          <button
            onClick={reset}
            data-testid="forca-reset-btn"
            className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-slate-400 hover:text-amber-300 hover:border-amber-400/40 hover:rotate-[-120deg] transition-all duration-300"
            aria-label="Reiniciar partida"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
